/// <amd-module name="Work/Stats/WorkMetricsSection" />

import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, Components, IVssComponentProperties } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import * as Resources from "Work/Stats/Resources";

interface WorkMetrics {
    workItemsCreated: number;
    workItemsCompleted: number;
}

interface WorkMetricsData {
    workMetrics: WorkMetrics | undefined;
}

const enum WorkPerfConstants {
    Area = "WorkMetricsSection",
    Scenario = "FetchWorkMetrics"
}

interface WorkMetricsSectionProps extends IVssComponentProperties {
    numOfDays: number;
}

class WorkMetricsSection extends VssComponent<WorkMetricsSectionProps, WorkMetricsData> {
    private readonly _serviceName: string | undefined;

    constructor(props: WorkMetricsSectionProps, context: IVssComponentContext) {
        super(props, context);

        const serviceInfo = context.pageContext.getService<IVssContributionService>("IVssContributionService")
            .getContribution<{ name: string; }>("ms.vss-work-web.work-service-navigation-info");

        if (serviceInfo) {
            this._serviceName = serviceInfo.name;
        }

        this.state = {
            workMetrics: undefined
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._fetchWITMetrics(this.props.numOfDays);
    }

    public componentDidUpdate(prevProps: WorkMetricsSectionProps, prevState: WorkMetricsData): void {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.numOfDays !== this.props.numOfDays) {
            this._fetchWITMetrics(this.props.numOfDays);
        }
    }

    public render(): JSX.Element {
        const { workMetrics } = this.state;

        return (
            <div className={css(this.props.className, "wit-metric-tile")}>
                {workMetrics && this._serviceName ? <WITMetricsContainer metricData={workMetrics} title={this._serviceName} /> : null}
            </div>
        );
    }

    private _fetchWITMetrics(numOfDays: number): void {
        const PROVIDER_ID = "ms.vss-work-web.work-item-metrics-data-provider-verticals";
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const properties = {
            numOfDays: numOfDays
        };
        performanceService.startScenario(WorkPerfConstants.Scenario, false);
        this.trackPromise(
            this.context.pageContext
                .getService<IVssContributionService>("IVssContributionService")
                .getDataAsync<WorkMetricsData>(PROVIDER_ID, properties)
        ).promise.then(
            (data: WorkMetricsData | undefined) => {
                if (data) {
                    this.setState(data);
                    performanceService.endScenario(WorkPerfConstants.Area, WorkPerfConstants.Scenario, false);
                } else {
                    this._endPerfScenario(performanceService);
                }
            },
            () => {
                this._endPerfScenario(performanceService);
            }
        );
    }

    private _endPerfScenario(performanceService: IVssPerformanceService): void {
        performanceService.endScenario(WorkPerfConstants.Area, WorkPerfConstants.Scenario, false, undefined, { status: "failed" });
    }
}

const WITMetricsContainer = (props: { metricData: WorkMetrics; title: string }): JSX.Element => {
    const { metricData, title } = props;

    return (
        <>
            <div className="body-l">{title}</div>
            <div className="work-stats flex">
                {getComponent("work-items-created work-stats-component", "Assign", metricData.workItemsCreated, Resources.WorkItemsCreated)}
                {getComponent(
                    "work-items-completed work-stats-component",
                    "WorkItem",
                    metricData.workItemsCompleted,
                    Resources.WorkItemsCompleted
                )}
            </div>
        </>
    );
};

const getComponent = (containerClassName: string, iconName: string, count: number, text?: string): JSX.Element => {
    return (
        <div className={css(containerClassName, "flex flex-grow")}>
            <Icon iconName={iconName} className={"wit-icon fontSizeML"} />
            <div className="count-and-text">
                <div className="title-xs"> {count > 1000 ? Resources.MoreThan1K : count} </div>
                <div className="text fontSize word-break">{text}</div>
            </div>
        </div>
    );
};

Components.add("WIT.WorkStats", WorkMetricsSection);
